import { CircleAlert, Info, Search } from "lucide-react"
import { SectionShell } from "@/components/design-system/SectionShell"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Progress } from "@/components/ui/progress"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

export function ShadcnPrimitivesSection() {
  return (
    <SectionShell
      id="shadcn-primitives"
      title="Shadcn primitives"
      description="Nova-style primitives installed from the requested tweakcn registry. They use the same semantic tokens as the operational app."
      preview={
        <div className="space-y-8">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem><BreadcrumbLink href="#">Projects</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbLink href="#">PRJ-024</BreadcrumbLink></BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem><BreadcrumbPage>Progress</BreadcrumbPage></BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <Tabs defaultValue="attention">
            <TabsList variant="line">
              <TabsTrigger value="attention">Attention</TabsTrigger>
              <TabsTrigger value="evidence">Evidence</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>
            <TabsContent value="attention" className="pt-5">
              <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
                <Card>
                  <CardHeader><CardTitle>Evidence coverage</CardTitle><CardDescription>Approved source material attached to reported progress.</CardDescription></CardHeader>
                  <CardContent><div className="flex items-baseline justify-between"><strong className="text-3xl tabular-nums">78%</strong><span className="text-xs text-muted-foreground">+12% this week</span></div><Progress value={78} className="mt-5" /></CardContent>
                  <CardFooter className="text-xs text-muted-foreground">14 m² still needs evidence</CardFooter>
                </Card>
                <div className="space-y-3">
                  <Alert><Info /><AlertTitle>Two records need review</AlertTitle><AlertDescription>Review source evidence before this value enters the client progress view.</AlertDescription></Alert>
                  <Alert variant="destructive"><CircleAlert /><AlertTitle>Rate gap blocks issue</AlertTitle><AlertDescription>One BOQ item has no approved build-up.</AlertDescription></Alert>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="evidence" className="pt-5"><Textarea aria-label="Evidence note" placeholder="Add a review note tied to this evidence…" /></TabsContent>
            <TabsContent value="history" className="pt-5"><div className="space-y-3"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-4/5" /><Skeleton className="h-12 w-3/5" /></div></TabsContent>
          </Tabs>

          <Separator />
          <div className="flex max-w-sm items-center gap-2">
            <Search className="size-4 text-muted-foreground" aria-hidden />
            <Input placeholder="Search records and commands" />
            <TooltipProvider><Tooltip><TooltipTrigger asChild><button type="button" className="rounded-md border px-2 py-1 text-xs text-muted-foreground">⌘K</button></TooltipTrigger><TooltipContent>Open command search</TooltipContent></Tooltip></TooltipProvider>
          </div>
        </div>
      }
      whenToUse={<ul><li>Use semantic primitives before composing one-off cards, tabs, feedback, or loading states.</li><li>Preserve their keyboard and focus behavior.</li></ul>}
      whenNotToUse={<ul><li>Do not wrap every content block in a card.</li><li>Do not use alerts for passive descriptive copy.</li></ul>}
      code={`<Card>…</Card>\n<Tabs defaultValue="attention">…</Tabs>\n<Alert>…</Alert>`}
    />
  )
}
